/**
 * Service for managing mention types in Firestore
 * Mention types define categories of mentions that can be issued
 * (e.g., "Behavior Mention", "Homework Mention", "Uniform Mention")
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase.js';

class MentionTypesService {
  constructor() {
    this.collectionName = 'mentionTypes';
    this.cache = null;
    this.cacheAll = null;
    this.cacheTimestamp = null;
    this.cacheSchoolId = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get collection reference
   * @param {string} schoolId - School ID (required for hierarchical collection)
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for hierarchical mentionTypes collection');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Check if cache is valid
   */
  isCacheValid(schoolId) {
    if (!this.cache || !this.cacheTimestamp || this.cacheSchoolId !== schoolId) return false;
    return Date.now() - this.cacheTimestamp < this.cacheDuration;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache = null;
    this.cacheAll = null;
    this.cacheTimestamp = null;
    this.cacheSchoolId = null;
  }

  /**
   * Get all mention types (with caching)
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of mention type objects
   */
  async getAll(schoolId) {
    if (this.isCacheValid(schoolId)) {
      return this.cache;
    }

    try {
      const snapshot = await getDocs(this.getCollectionRef(schoolId));

      const types = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id
          };
        })
        .filter(type => type.active !== false && type.retired !== true);

      // Sort by sortOrder (descending)
      const sorted = types.sort((a, b) => {
        const orderA = a.sortOrder ?? 0;
        const orderB = b.sortOrder ?? 0;
        if (orderA !== orderB) return orderB - orderA;
        return (a.label || '').localeCompare(b.label || '');
      });

      this.cacheAll = null;
      this.cache = sorted;
      this.cacheTimestamp = Date.now();
      this.cacheSchoolId = schoolId;

      return sorted;
    } catch (error) {
      console.error('Error fetching mention types:', error);
      throw error;
    }
  }

  /**
   * Get all mention types including inactive ones
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Array>} Array of all mention type objects
   */
  async getAllIncludingInactive(schoolId) {
    if (this.isCacheValid(schoolId) && this.cacheAll) {
      return this.cacheAll;
    }

    try {
      const snapshot = await getDocs(this.getCollectionRef(schoolId));

      const types = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id
        };
      });

      const sorted = types.sort((a, b) => {
        const orderA = a.sortOrder ?? 0;
        const orderB = b.sortOrder ?? 0;
        if (orderA !== orderB) return orderB - orderA;
        return (a.label || '').localeCompare(b.label || '');
      });

      this.cacheAll = sorted;
      this.cache = sorted.filter(type => type.active !== false && type.retired !== true);
      this.cacheTimestamp = Date.now();
      this.cacheSchoolId = schoolId;

      return sorted;
    } catch (error) {
      console.error('Error fetching all mention types:', error);
      throw error;
    }
  }

  /**
   * Get single mention type by ID
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Object|null>} Mention type object or null
   */
  async getById(id, schoolId) {
    try {
      const docRef = schoolId
        ? doc(db, 'schools', schoolId, this.collectionName, id)
        : doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          ...docSnap.data(),
          id: docSnap.id
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching mention type:', error);
      throw error;
    }
  }

  /**
   * Get mention type by value field
   * @param {string} value - The value field to search for
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<Object|null>} Mention type object or null
   */
  async getByValue(value, schoolId) {
    const types = await this.getAll(schoolId);
    return types.find(type => type.value === value) || null;
  }

  /**
   * Get Firestore document ID by value field
   * @param {string} value - The value field to search for
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<string|null>} Document ID or null
   */
  async getIdByValue(value, schoolId) {
    const type = await this.getByValue(value, schoolId);
    return type?.id || null;
  }

  /**
   * Create a new mention type
   * @param {Object} data - Mention type data
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<string>} New document ID
   */
  async create(data, schoolId) {
    try {
      const docData = {
        ...data,
        active: data.active !== undefined ? data.active : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getCollectionRef(schoolId), docData);
      this.clearCache();
      return docRef.id;
    } catch (error) {
      console.error('Error creating mention type:', error);
      throw error;
    }
  }

  /**
   * Update an existing mention type
   * @param {string} id - Document ID
   * @param {Object} data - Updated data
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<void>}
   */
  async update(id, data, schoolId) {
    try {
      const docRef = doc(db, 'schools', schoolId, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      this.clearCache();
    } catch (error) {
      console.error('Error updating mention type:', error);
      throw error;
    }
  }

  /**
   * Delete a mention type
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<void>}
   */
  async delete(id, schoolId) {
    try {
      const docRef = doc(db, 'schools', schoolId, this.collectionName, id);
      await deleteDoc(docRef);
      this.clearCache();
    } catch (error) {
      console.error('Error deleting mention type:', error);
      throw error;
    }
  }

  /**
   * Soft delete - mark as inactive
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<void>}
   */
  async deactivate(id, schoolId) {
    await this.update(id, { active: false }, schoolId);
  }

  /**
   * Reactivate a mention type
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<void>}
   */
  async activate(id, schoolId) {
    await this.update(id, { active: true }, schoolId);
  }

  /**
   * Update sort order for multiple items
   * @param {Array<{id: string, sortOrder: number}>} items - Items with new sort orders
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<void>}
   */
  async updateSortOrder(items, schoolId) {
    try {
      const batch = writeBatch(db);

      items.forEach(({ id, sortOrder }) => {
        const docRef = doc(db, 'schools', schoolId, this.collectionName, id);
        batch.update(docRef, {
          sortOrder,
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      this.clearCache();
    } catch (error) {
      console.error('Error updating sort order:', error);
      throw error;
    }
  }

  /**
   * Seed initial mention types
   * @param {Array} types - Array of mention type objects
   * @param {string} schoolId - School ID (required for hierarchical collection)
   * @returns {Promise<void>}
   */
  async seedInitialData(types, schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for seeding mentionTypes');
    }
    
    try {
      const batch = writeBatch(db);

      types.forEach((typeData, index) => {
        const docRef = doc(this.getCollectionRef(schoolId));
        batch.set(docRef, {
          ...typeData,
          sortOrder: typeData.sortOrder !== undefined ? typeData.sortOrder : index,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      this.clearCache();
      console.log(`Successfully seeded ${types.length} mention types for school ${schoolId}`);
    } catch (error) {
      console.error('Error seeding mention types:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const mentionTypesService = new MentionTypesService();
export default mentionTypesService;
